<template>
  <div class="virtual-keyboard">
    <div class="keyboard-rows">
      <div 
        v-for="(row, rowIndex) in layout" 
        :key="rowIndex" 
        class="keyboard-row"
      >
        <template v-for="(key, kIndex) in row">
          <button
            v-if="key !== ''"
            :key="`${rowIndex}-${kIndex}`"
            @click="handleKeyPress(key)"
            class="keyboard-key"
            :class="{ 'keyboard-key-numpad': isNumpad }"
          >
            {{ key }}
          </button>
          <div
            v-else
            :key="`spacer-${rowIndex}-${kIndex}`"
            class="keyboard-spacer"
          />
        </template>
      </div>
    </div>
    
    <!-- Action Row -->
    <div
      class="keyboard-actions"
      :class="{ 'keyboard-actions-numpad': isNumpad }"
    >
      <button
        v-if="!isNumpad"
        @click="handleKeyPress(' ')"
        class="keyboard-key keyboard-key-space"
      >
        SPACE
      </button>
      
      <button
        @click="handleBackspace"
        class="keyboard-key keyboard-key-backspace"
        :class="{ 'keyboard-key-numpad': isNumpad }"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
          <line
            x1="18"
            y1="9"
            x2="12"
            y2="15"
          />
          <line
            x1="12"
            y1="9"
            x2="18"
            y2="15"
          />
        </svg>
      </button>
      
      <button
        @click="handleEnter"
        class="keyboard-key keyboard-key-enter"
        :class="{ 'keyboard-key-numpad': isNumpad }"
      >
        <svg
          v-if="isNumpad"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <svg
          v-else
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <line
            x1="5"
            y1="12"
            x2="19"
            y2="12"
          />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VirtualKeyboard',
  props: {
    mode: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'numpad'].includes(value)
    }
  },
  data() {
    return {
      KEYS: [
        ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
        ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
        ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
        ['Z', 'X', 'C', 'V', 'B', 'N', 'M']
      ],
      NUMPAD_KEYS: [
        ['1', '2', '3'],
        ['4', '5', '6'],
        ['7', '8', '9'],
        ['', '0', '']
      ]
    };
  },
  computed: {
    isNumpad() {
      return this.mode === 'numpad';
    },
    layout() {
      return this.isNumpad ? this.NUMPAD_KEYS : this.KEYS;
    }
  },
  methods: {
    handleKeyPress(key) {
      this.$emit('keyPress', key);
    },
    handleBackspace() {
      this.$emit('backspace');
    },
    handleEnter() {
      this.$emit('enter');
    }
  }
};
</script>

<style scoped>
.virtual-keyboard {
  background: #E5E7EB;
  padding: 12px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  user-select: none;
}

.keyboard-rows {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: flex-end;
}

.keyboard-row {
  display: flex;
  justify-content: center;
  gap: 8px;
  width: 100%;
}

.keyboard-key {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.1);
  font-weight: bold;
  color: #1F2937;
  border: none;
  cursor: pointer;
  transition: all 0.1s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  max-width: 10%;
  height: 56px;
  font-size: 20px;
}

.keyboard-key:hover {
  background: #F9FAFB;
}

.keyboard-key:active {
  background: #F3F4F6;
  box-shadow: none;
  transform: translateY(2px);
}

.keyboard-key-numpad {
  height: 80px;
  font-size: 32px;
  max-width: none;
  flex: 1;
}

.keyboard-key-space {
  flex: 4;
  max-width: none;
}

.keyboard-key-backspace {
  background: #D1D5DB;
  color: #374151;
  min-width: 60px;
}

.keyboard-key-backspace:hover {
  background: #E5E7EB;
}

.keyboard-key-enter {
  background: #2563EB;
  color: white;
  min-width: 60px;
}

.keyboard-key-enter:hover {
  background: #1D4ED8;
}

.keyboard-key-enter:active {
  background: #1E40AF;
}

.keyboard-spacer {
  flex: 1;
}

.keyboard-actions {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
  height: 56px;
}

.keyboard-actions-numpad {
  height: 80px;
}
</style>
